/*
  Zone finder: a USDA hardiness zone map with ZIP search and click-to-identify.

  - The map (assets/zone-map.svg) is one path per half zone, drawn from the
    2023 USDA Plant Hardiness Zone Map data (PRISM Climate Group, Oregon State
    University). Loaded after the page, then inlined so it can be styled.
  - A ZIP search looks the ZIP up in assets/zip-zones.json, fetched only on
    the first search. Nothing leaves the visitor's browser.
  - Clicking the map reads the zone from the shape that was clicked.
  - A zone found by ZIP (never the ZIP itself) is saved in localStorage for
    other pages to read. The finder never preselects it: each visit starts
    blank, and map clicks are exploring, so they are not saved.
*/
(function () {
  var roots = document.querySelectorAll('[data-zone-finder]');
  if (!roots.length) return;

  var HALF = [];
  for (var n = 1; n <= 13; n++) { HALF.push(n + 'a', n + 'b'); }
  var B36 = '0123456789abcdefghijklmnopqrstuvwxyz';
  var STORE_KEY = 'eureka-zone';
  var zipData = null;

  function store(zone) { try { localStorage.setItem(STORE_KEY, zone); } catch (e) {} }

  function loadZips(url) {
    if (zipData) return Promise.resolve(zipData);
    return fetch(url).then(function (r) { return r.json(); }).then(function (d) { zipData = d; return d; });
  }

  function lookup(zip) {
    var i = parseInt(zip, 10) * 5;
    var rec = zipData.substr(i, 5);
    if (!rec || rec.charAt(0) === '.') return null;
    var zone = HALF[rec.charCodeAt(0) - 97];
    var xy = rec.substr(1);
    if (xy === 'zzzz') return { zone: zone };
    return {
      zone: zone,
      x: B36.indexOf(xy.charAt(0)) * 36 + B36.indexOf(xy.charAt(1)),
      y: B36.indexOf(xy.charAt(2)) * 36 + B36.indexOf(xy.charAt(3))
    };
  }

  roots.forEach(function (root) {
    var mapBox = root.querySelector('[data-zf-map]');
    var form = root.querySelector('[data-zf-form]');
    var input = root.querySelector('[data-zf-zip]');
    var error = root.querySelector('[data-zf-error]');
    var result = root.querySelector('[data-zf-result]');
    var reset = root.querySelector('[data-zf-reset]');
    var hint = root.querySelector('[data-zf-hint]');
    var tip = root.querySelector('[data-zf-tip]');
    var wrap = mapBox.parentElement;
    var hovered = null;
    var svg, pin, zones = {}, view = { x: 0, y: 0, w: 800, h: 502 }, full = null, anim;

    fetch(root.dataset.mapUrl).then(function (r) { return r.text(); }).then(function (text) {
      mapBox.innerHTML = text;
      svg = mapBox.querySelector('svg');
      var vb = svg.getAttribute('viewBox').split(' ').map(Number);
      full = { x: vb[0], y: vb[1], w: vb[2], h: vb[3] };
      view = Object.assign({}, full);
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', 'Map of the United States coloured by USDA plant hardiness zone. Select a point to see its zone.');
      svg.querySelectorAll('#zones path').forEach(function (p) { zones[p.getAttribute('data-zone')] = p; });
      pin = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      pin.setAttribute('class', 'zf-pin');
      pin.innerHTML = '<circle class="zf-pin__pulse" r="8"></circle><circle class="zf-pin__dot" r="4.5"></circle>';
      svg.appendChild(pin);
      svg.addEventListener('click', onMapClick);
      if (window.matchMedia('(hover: hover)').matches) {
        svg.addEventListener('mousemove', onHover);
        svg.addEventListener('mouseleave', clearHover);
      }
      root.classList.add('is-ready');
    });

    function setView(target, instant) {
      cancelAnimationFrame(anim);
      var from = Object.assign({}, view), t0 = null, dur = instant ? 0 : 650;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var k = dur ? Math.min(1, (ts - t0) / dur) : 1;
        var e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        ['x', 'y', 'w', 'h'].forEach(function (p) { view[p] = from[p] + (target[p] - from[p]) * e; });
        svg.setAttribute('viewBox', [view.x, view.y, view.w, view.h].join(' '));
        root.style.setProperty('--zf-scale', (full.w / view.w).toFixed(3));
        if (k < 1) anim = requestAnimationFrame(step);
      }
      anim = requestAnimationFrame(step);
    }

    function zoomTo(x, y) {
      var w = full.w / 2.2, h = full.h / 2.2;
      var nx = Math.max(full.x, Math.min(x - w / 2, full.x + full.w - w));
      var ny = Math.max(full.y, Math.min(y - h / 2, full.y + full.h - h));
      setView({ x: nx, y: ny, w: w, h: h });
    }

    function highlight(zone) {
      var family = zone ? zone.replace(/[ab]$/, '') : null;
      Object.keys(zones).forEach(function (z) {
        zones[z].classList.toggle('is-match', !!family && z.replace(/[ab]$/, '') === family);
      });
      svg.classList.toggle('is-focused', !!family);
    }

    function placePin(x, y) {
      if (x == null) { pin.classList.remove('is-visible'); return; }
      pin.setAttribute('transform', 'translate(' + x + ' ' + y + ')');
      pin.classList.add('is-visible');
    }

    function show(r) {
      var family = r.zone.replace(/[ab]$/, '');
      var trange = '';
      if (/^\d+$/.test(r.zone)) {
        var lo = zones[r.zone + 'a'], hi = zones[r.zone + 'b'];
        if (lo && hi) trange = lo.getAttribute('data-trange').split(' to ')[0] + ' to ' + hi.getAttribute('data-trange').split(' to ')[1];
      } else if (zones[r.zone]) {
        trange = zones[r.zone].getAttribute('data-trange');
      }
      var where = r.zip ? r.zip + ' is in' : (r.estimated ? 'This spot is in' : 'This is');
      result.innerHTML =
        '<p class="zf-result__eyebrow">Your hardiness zone</p>' +
        '<p class="zf-result__zone">' + where + ' <strong>zone ' + r.zone + '</strong></p>' +
        (trange ? '<p class="zf-result__temp">The average coldest night of the year is ' + trange + '°F.</p>' : '') +
        '<p class="zf-result__note">Everywhere in zone ' + family + ' is highlighted on the map.' +
        (r.estimated ? ' Picked from the map, so treat it as an estimate.' : '') + '</p>';
      result.hidden = false;
      highlight(r.zone);
      if (r.x != null) { placePin(r.x, r.y); zoomTo(r.x, r.y); }
      else { placePin(null); setView(full); }
      reset.hidden = false;
      if (r.zip) store(r.zone);
    }

    function hideHint() { if (hint) hint.classList.add('is-gone'); }

    function clearHover() {
      if (hovered) hovered.classList.remove('is-hover');
      hovered = null;
      tip.hidden = true;
    }

    function onHover(e) {
      var target = e.target.closest ? e.target.closest('#zones path') : null;
      if (!target) { clearHover(); return; }
      if (target !== hovered) {
        if (hovered) hovered.classList.remove('is-hover');
        hovered = target;
        hovered.classList.add('is-hover');
        tip.innerHTML = '<strong>Zone ' + target.getAttribute('data-zone') + '</strong> · ' + target.getAttribute('data-trange') + '°F';
      }
      var r = wrap.getBoundingClientRect();
      tip.style.left = (e.clientX - r.left) + 'px';
      tip.style.top = (e.clientY - r.top) + 'px';
      tip.hidden = false;
    }

    function onMapClick(e) {
      var target = e.target.closest ? e.target.closest('#zones path') : null;
      if (!target) return;
      var pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      var p = pt.matrixTransform(svg.getScreenCTM().inverse());
      error.hidden = true;
      hideHint();
      show({ zone: target.getAttribute('data-zone'), x: p.x, y: p.y, estimated: true });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var zip = (input.value || '').trim();
      if (!/^\d{5}$/.test(zip)) {
        error.textContent = 'Enter a five-digit ZIP code.';
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        return;
      }
      input.removeAttribute('aria-invalid');
      error.hidden = true;
      loadZips(root.dataset.zipUrl).then(function () {
        var r = lookup(zip);
        if (!r) {
          error.textContent = 'We don’t have a zone for ' + zip + '. Try a nearby ZIP code, or tap the map where you live.';
          error.hidden = false;
          return;
        }
        r.zip = zip;
        hideHint();
        show(r);
      });
    });

    root.querySelectorAll('[data-zf-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var zone = chip.getAttribute('data-zf-chip');
        error.hidden = true;
        show({ zone: zone });
      });
    });

    reset.addEventListener('click', function () {
      highlight(null); placePin(null); setView(full);
      result.hidden = true; reset.hidden = true;
      try { localStorage.removeItem(STORE_KEY); } catch (e) {}
      input.value = ''; input.focus();
    });
  });
})();
